import React, {useCallback, useEffect, useState} from 'react';
import {
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Button,
    Divider,
    IconButton,
    Link,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import NotesOutlinedIcon from '@mui/icons-material/NotesOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import {useNavigate} from 'react-router';
import {useSession} from '../../../firebase/UserProvider';
import {getUsersByIds} from '../../../firebase/firestore/users';
import {getRawStreamUrl} from '../../products/reportEditorPage/leftPanel/components/sourcesApi';
import AuthorChip from '../../products/components/common/AuthorChip';
import {deleteCollectionMaterial} from '../collectionTaskApi';
import EntityTagsSection, {TAGS_SECTION_HEADING_SX} from '../../collection-requests/tags/EntityTagsSection';
import {getClassificationByValue} from '../../../components/prosemirror-editor/helpers/classificationLevels';
import CollectionMaterialClassificationDialog from './CollectionMaterialClassificationDialog';

function statusChip(status) {
    if (status === 'submitted') {
        return (
            <Chip
                size="small"
                icon={<CheckCircleOutlineIcon style={{fontSize: 14}}/>}
                label="Submitted"
                color="success"
                variant="outlined"
                sx={{height: 20, alignSelf: 'flex-start'}}
            />
        );
    }
    return (
        <Chip
            size="small"
            icon={<RadioButtonUncheckedIcon style={{fontSize: 14}}/>}
            label="Draft"
            variant="outlined"
            sx={{height: 20, alignSelf: 'flex-start'}}
        />
    );
}

function SourceKindIcon({sourceKind}) {
    if (sourceKind === 'url') return <LinkOutlinedIcon fontSize="small"/>;
    if (sourceKind === 'raw') return <NotesOutlinedIcon fontSize="small"/>;
    return <InsertDriveFileOutlinedIcon fontSize="small"/>;
}

function sourceKindLabel(sourceKind) {
    if (sourceKind === 'url') return 'URL';
    if (sourceKind === 'raw') return 'Raw text';
    if (sourceKind === 'file') return 'File';
    return sourceKind || '—';
}

function formatDate(value) {
    return value ? new Date(value).toLocaleString() : '—';
}

function userDisplayName(u) {
    if (!u) return null;
    const fromParts = [u.firstName, u.lastName].filter(Boolean).join(' ').trim();
    return fromParts || u.displayName || u.name || u.email || null;
}

function materialTitle(material) {
    return material?.title && material.title !== material.cmNumber
        ? material.title
        : 'Untitled material';
}

/**
 * Gray metadata header for Collected Materials, modeled on the FINTEL
 * DocumentHeader gray stack (#F9F9F9). Rendered above the file content in the
 * CM viewer modal.
 */
export default function CollectionMaterialHeader({
    material,
    canEditTags = false,
    onDeleted,
    onMaterialUpdated,
}) {
    const navigate = useNavigate();
    const {user} = useSession() || {};
    const userId = user?.uid || null;
    const [authorProfile, setAuthorProfile] = useState(null);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [classificationDialogOpen, setClassificationDialogOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [actionError, setActionError] = useState(null);

    const createdBy = material?.createdBy || null;
    useEffect(() => {
        let cancelled = false;
        setAuthorProfile(null);
        if (!createdBy) return undefined;
        getUsersByIds([createdBy])
            .then((users) => {
                if (!cancelled) setAuthorProfile(users?.[0] || null);
            })
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, [createdBy]);

    const canDelete = Boolean(material?.canDelete);
    const canEditClassification = Boolean(material?.editable);
    const streamId = material?.dataItemId != null ? String(material.dataItemId) : null;

    const handleDownload = useCallback(() => {
        if (!streamId || !userId) return;
        const url = getRawStreamUrl({referenceId: streamId, userId, disposition: 'attachment'});
        if (url) window.open(url, '_blank', 'noopener,noreferrer');
    }, [streamId, userId]);

    const handleDelete = useCallback(async () => {
        if (!material?.id) return;
        setBusy(true);
        setActionError(null);
        try {
            await deleteCollectionMaterial(material.id);
            setDeleteConfirmOpen(false);
            onDeleted?.();
        } catch (e) {
            setActionError(e?.message || 'Failed to delete material');
            setBusy(false);
        }
    }, [material?.id, onDeleted]);

    if (!material) return null;

    const materialLevel = material.materialClassification?.level || null;
    const materialDistribution = material.materialClassification?.distribution || [];
    const materialCompartment = material.materialClassification?.compartment || [];
    const materialLevelInfo = materialLevel ? getClassificationByValue(materialLevel) : null;

    const fields = [
        {
            key: 'cmNumber',
            label: 'CM #',
            render: () => (
                <Typography variant="body2" sx={{fontFamily: 'monospace'}}>
                    {material.cmNumber || material.id}
                </Typography>
            ),
        },
        {
            key: 'collectedBy',
            label: 'Collected by',
            render: () => (material.createdBy ? (
                <AuthorChip
                    userId={material.createdBy}
                    userDisplayName={userDisplayName(authorProfile) || material.createdBy}
                    avatarProfile={authorProfile}
                    flex={undefined}
                />
            ) : (
                <Typography variant="body2">—</Typography>
            )),
        },
        {
            key: 'ctNumber',
            label: 'Collection Task',
            render: () => (material.collectionTaskId ? (
                <Link
                    component="button"
                    type="button"
                    underline="hover"
                    onClick={() => navigate(
                        `/collection/tasks/${encodeURIComponent(material.ctNumber || material.collectionTaskId)}`,
                    )}
                    sx={{fontFamily: 'monospace', textAlign: 'left'}}
                >
                    {material.ctNumber || material.collectionTaskId}
                </Link>
            ) : (
                <Typography variant="body2">{material.ctNumber || '—'}</Typography>
            )),
        },
        {
            key: 'createdAt',
            label: 'Created',
            render: () => <Typography variant="body2">{formatDate(material.createdAt)}</Typography>,
        },
        {
            key: 'status',
            label: 'Status',
            render: () => (
                <Stack spacing={0.4}>
                    {statusChip(material.status)}
                    {material.uploadStatus === 'processing' && (
                        <Chip
                            size="small"
                            label="Reprocessing…"
                            color="warning"
                            variant="outlined"
                            sx={{height: 20, alignSelf: 'flex-start'}}
                        />
                    )}
                </Stack>
            ),
        },
        {
            key: 'submittedAt',
            label: 'Submitted',
            render: () => (
                <Typography variant="body2" sx={{whiteSpace: 'nowrap'}}>
                    {material.submittedAt ? formatDate(material.submittedAt) : '—'}
                </Typography>
            ),
        },
        ...(materialLevel ? [{
            key: 'classification',
            label: 'Classification',
            render: () => (
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap alignItems="center">
                    <Chip
                        size="small"
                        label={materialLevelInfo ? materialLevelInfo.value : materialLevel}
                        sx={{
                            height: 20,
                            fontWeight: 700,
                            color: materialLevelInfo?.color,
                            bgcolor: materialLevelInfo?.bgColor,
                        }}
                    />
                    {[...materialDistribution, ...materialCompartment].map((tag) => (
                        <Chip key={tag} size="small" variant="outlined" label={tag} sx={{height: 20}}/>
                    ))}
                </Stack>
            ),
        }] : []),
        {
            key: 'updatedAt',
            label: 'Updated',
            render: () => (
                <Stack direction="row" spacing={0.25} alignItems="center" flexWrap="nowrap">
                    <Typography variant="body2" sx={{whiteSpace: 'nowrap'}}>
                        {formatDate(material.updatedAt)}
                    </Typography>
                    <Tooltip
                        title={canEditClassification
                            ? 'Edit classification'
                            : 'Submitted materials are locked — reopen via "Modify" in the material editor to change classification'}
                        arrow
                        placement="top"
                    >
                        <span>
                            <IconButton
                                size="small"
                                aria-label="Edit classification"
                                disabled={busy || !canEditClassification}
                                onClick={() => setClassificationDialogOpen(true)}
                            >
                                <EditOutlinedIcon fontSize="small"/>
                            </IconButton>
                        </span>
                    </Tooltip>
                    {canDelete && (
                        <Tooltip title="Delete" arrow placement="top">
                            <IconButton
                                size="small"
                                color="error"
                                aria-label="Delete"
                                disabled={busy}
                                onClick={() => setDeleteConfirmOpen(true)}
                            >
                                <DeleteOutlineIcon fontSize="small"/>
                            </IconButton>
                        </Tooltip>
                    )}
                </Stack>
            ),
        },
    ];

    return (
        <>
            <Stack spacing={1.5} p={2} data-testid="cm-viewer-header">
                <Stack spacing={1.5} p={2} bgcolor="#F9F9F9" borderRadius={1}>
                    {/* Task title + download action row */}
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        spacing={1}
                        flexWrap="wrap"
                        useFlexGap
                    >
                        <Typography
                            variant="subtitle1"
                            sx={{fontWeight: 700, flex: 1, minWidth: 0, wordBreak: 'break-word'}}
                        >
                            {materialTitle(material)}
                        </Typography>
                        {streamId && (
                            <Tooltip title="Download" arrow placement="top">
                                <span>
                                    <IconButton
                                        size="small"
                                        aria-label="Download"
                                        disabled={!userId}
                                        onClick={handleDownload}
                                    >
                                        <DownloadOutlinedIcon fontSize="small"/>
                                    </IconButton>
                                </span>
                            </Tooltip>
                        )}
                    </Stack>

                    <Divider sx={{my: 0}}/>

                    <Stack
                        direction="row"
                        flexWrap="wrap"
                        useFlexGap
                        rowGap={1.25}
                        sx={{width: '100%'}}
                    >
                        {fields.map(({key, label, render}) => (
                            <Stack key={key} spacing={0.25} sx={{flex: 1, minWidth: 80}}>
                                <Typography sx={TAGS_SECTION_HEADING_SX}>{label}</Typography>
                                {render()}
                            </Stack>
                        ))}
                    </Stack>

                    <Divider sx={{my: 0.5}}/>

                    <EntityTagsSection
                        entityType="CM"
                        entityId={material.id}
                        canEdit={canEditTags}
                        enabled={Boolean(material.id)}
                        dense
                    />
                </Stack>
                {actionError && (
                    <Typography variant="body2" color="error">{actionError}</Typography>
                )}
            </Stack>

            <Dialog open={deleteConfirmOpen} onClose={() => !busy && setDeleteConfirmOpen(false)}>
                <DialogTitle>Delete collected material?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        This removes {material.cmNumber || 'this material'} and its linked uploaded
                        content from search. This cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button disabled={busy} onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
                    <Button color="error" variant="contained" disabled={busy} onClick={handleDelete}>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            <CollectionMaterialClassificationDialog
                open={classificationDialogOpen}
                onClose={() => setClassificationDialogOpen(false)}
                material={material}
                onSaved={(updated) => onMaterialUpdated?.(updated)}
            />
        </>
    );
}
